import React, { useState, useEffect } from 'react';
import { useEquipment } from '../../../context/EquipmentContext';
import { EQUIPMENT_TYPES } from '../../../data/nurseAppModels';

const EquipmentAssignmentModal = ({
  isOpen,
  onClose,
  shift,
  planId,
  planVersion,
  existingAssignments = [],
  onAssign
}) => {
  const { equipment, catalogs } = useEquipment();
  const [selectedEquipment, setSelectedEquipment] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState('');
  const [filter, setFilter] = useState('all'); // all, small, large
  const [searchTerm, setSearchTerm] = useState('');

  // Filter equipment
  const filteredEquipment = equipment.filter(eq => {
    const matchesFilter = filter === 'all' || 
      (filter === 'small' && eq.type === EQUIPMENT_TYPES.SMALL_ITEM) ||
      (filter === 'large' && eq.type === EQUIPMENT_TYPES.LARGE_ITEM);
    const matchesSearch = !searchTerm || 
      eq.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      eq.code?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch && eq.status === 'AVAILABLE';
  });

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSelectedEquipment('');
      setQuantity(1);
      setNotes('');
      setSearchTerm('');
    }
  }, [isOpen]);

  // Get selected equipment details
  const selectedEquipmentData = equipment.find(eq => eq.id === selectedEquipment);

  // Handle submit
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedEquipment) {
      alert('กรุณาเลือกอุปกรณ์');
      return;
    }

    if (selectedEquipmentData?.type === EQUIPMENT_TYPES.SMALL_ITEM && quantity <= 0) {
      alert('กรุณาระบุจำนวนที่ถูกต้อง');
      return;
    }

    onAssign(selectedEquipment, {
      quantity: selectedEquipmentData?.type === EQUIPMENT_TYPES.SMALL_ITEM ? quantity : 1,
      notes: notes.trim() || null
    });
  };

  // Check if equipment already assigned
  const isAlreadyAssigned = (equipmentId) => {
    return existingAssignments.some(a => a.equipmentId === equipmentId);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold text-gray-800">กำหนดอุปกรณ์ให้กับเวร</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-2xl"
            >
              ×
            </button>
          </div>
          {shift && (
            <p className="text-sm text-gray-600 mt-2">
              เวร{shift.shiftType === 'MORNING' ? 'เช้า' : shift.shiftType === 'AFTERNOON' ? 'บ่าย' : 'ดึก'} - {shift.assignedToName}
            </p>
          )}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Search and Filter */}
          <div className="space-y-3">
            <input
              type="text"
              placeholder="ค้นหาอุปกรณ์..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setFilter('all')}
                className={`px-4 py-2 rounded-lg text-sm ${
                  filter === 'all' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                ทั้งหมด
              </button>
              <button
                type="button"
                onClick={() => setFilter('small')}
                className={`px-4 py-2 rounded-lg text-sm ${
                  filter === 'small' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                ชิ้นเล็ก
              </button>
              <button
                type="button"
                onClick={() => setFilter('large')}
                className={`px-4 py-2 rounded-lg text-sm ${
                  filter === 'large' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                ชิ้นใหญ่
              </button>
            </div>
          </div>

          {/* Equipment List */}
          <div className="border border-gray-200 rounded-lg max-h-64 overflow-y-auto">
            {filteredEquipment.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <p>ไม่พบอุปกรณ์</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {filteredEquipment.map(eq => {
                  const isAssigned = isAlreadyAssigned(eq.id);
                  const isSelected = selectedEquipment === eq.id;

                  return (
                    <div
                      key={eq.id}
                      onClick={() => !isAssigned && setSelectedEquipment(eq.id)}
                      className={`p-4 cursor-pointer transition ${
                        isSelected
                          ? 'bg-blue-50 border-l-4 border-blue-500'
                          : isAssigned
                          ? 'bg-gray-100 opacity-50 cursor-not-allowed'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <p className="font-medium text-gray-800">
                            {eq.name} ({eq.code})
                          </p>
                          <p className="text-sm text-gray-600 mt-1">
                            {eq.type === EQUIPMENT_TYPES.SMALL_ITEM ? 'ชิ้นเล็ก' : 'ชิ้นใหญ่'}
                            {eq.type === EQUIPMENT_TYPES.SMALL_ITEM && eq.currentStock !== null && (
                              <span className="ml-2">Stock: {eq.currentStock} {eq.unit}</span>
                            )}
                          </p>
                          {isAssigned && (
                            <p className="text-xs text-yellow-600 mt-1">⚠️ กำหนดแล้ว</p>
                          )}
                        </div>
                        {isSelected && (
                          <span className="text-blue-600 text-xl">✓</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Selected Equipment Details */}
          {selectedEquipmentData && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-800 mb-2">อุปกรณ์ที่เลือก</h3>
              <p className="text-sm text-gray-700">
                {selectedEquipmentData.name} ({selectedEquipmentData.code})
              </p>
              
              {selectedEquipmentData.type === EQUIPMENT_TYPES.SMALL_ITEM && (
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    จำนวน ({selectedEquipmentData.unit})
                  </label>
                  <input
                    type="number"
                    min="1"
                    max={selectedEquipmentData.currentStock || 999}
                    value={quantity}
                    onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {selectedEquipmentData.currentStock !== null && (
                    <p className="text-xs text-gray-500 mt-1">
                      Stock ที่มี: {selectedEquipmentData.currentStock} {selectedEquipmentData.unit}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              หมายเหตุ (ไม่บังคับ)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="เพิ่มหมายเหตุ..."
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              ยกเลิก
            </button>
            <button
              type="submit"
              disabled={!selectedEquipment}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              บันทึก
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EquipmentAssignmentModal;




